import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';
import { avecCle } from '../services/cleLecture';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

// ── Espace ressource « Mon espace » (contrat back /resources/me) ─────────────
//   GET  /resources/me            → { nom, prenom, client, mission_titre, besoin_id, date_debut, date_fin, taux_activite, statut }
//   GET  /resources/me/cra?month= → { month, days:{ 'YYYY-MM-DD': 1|0.5 }, total, status: draft|submitted|client_validated, locked, submitted_at, validated_at }
//   PUT  /resources/me/cra?month= → { days } (grille COMPLÈTE) | + { submit:true|false } ;
//        mois verrouillé → 423, saisie sur absence → 409 + conflits[] — messages à afficher tels quels
//   GET/POST /resources/me/leaves → { date_debut, date_fin, type?: conges|maladie|autre, motif? } → 201 pending
//   GET/POST /resources/me/frais  → { label, montant, date?, devise? } ou multipart + champ fichier `justificatif`
//   GET  /resources/me/documents  → documents de la fiche (is_principal en premier)

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const GHOST_BTN = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const PRIMARY_BTN = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const INPUT = { boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '10px 12px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
const LBL = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
const Chip = ({ kind = 'mut', title, style, children }) => (
  <span className={`src-chip src-chip--${kind}`} title={title} style={style}>{children}</span>
);

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const WD = ['LU', 'MA', 'ME', 'JE', 'VE', 'SA', 'DI'];
const mKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const mLabel = (key) => { const [y, m] = key.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const shiftMonth = (key, delta) => { const [y, m] = key.split('-').map(Number); return mKey(new Date(y, m - 1 + delta, 1)); };
const monthDays = (key) => { const [y, m] = key.split('-').map(Number); return new Date(y, m, 0).getDate(); };
const firstDayPad = (key) => { const [y, m] = key.split('-').map(Number); return (new Date(y, m - 1, 1).getDay() + 6) % 7; }; // lundi = 0
const isWeekend = (key, d) => { const [y, m] = key.split('-').map(Number); const wd = new Date(y, m - 1, d).getDay(); return wd === 0 || wd === 6; };
const isoOf = (key, d) => `${key}-${String(d).padStart(2, '0')}`;
const craUnitOf = (value) => ['heure', 'hour', 'hours', 'hourly', 'hourly_rate'].includes(String(value || '').toLowerCase()) ? 'heure' : 'jour';
const craUnitFrom = (...sources) => { for (const source of sources) { const value = source?.unite_facturation || source?.facturation_unite || source?.cra_unit || source?.billing_unit || source?.unit || source?.uniteFacturation; if (value) return craUnitOf(value); } return 'jour'; }; // clés du contrat : dates ISO complètes
const fmtDate = (iso) => { if (!iso) return ''; const t = new Date(iso); return Number.isNaN(t.getTime()) ? iso : t.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); };

const CRA_STATUS = {
  draft:              { l: 'En saisie',          k: 'mut' },
  submitted:          { l: 'Transmis — en validation', k: 'warn' },
  signature_en_cours: { l: 'En signature chez le client', k: 'warn' },
  client_validated:   { l: 'Validé par le client', k: 'ok' },
  valide_interne:     { l: 'Validé — transmis à la compta', k: 'ok' },
};
// Types de congé de REPLI — le catalogue vient du serveur quand il existe
// (GET /resources/me/leave-types, PROMPT BACK) : un type ajouté côté org
// (congé maternité…) apparaît alors sans aucun changement front.
const LEAVE_TYPES = [['conges', 'Congés'], ['maladie', 'Maladie'], ['autre', 'Autre']];
const normLeaveTypes = (srv) => {
  const repli = LEAVE_TYPES.map(([value, label]) => ({ value, label }));
  if (!Array.isArray(srv) || srv.length === 0) return repli;
  const mapped = srv
    .filter(t => t && t.actif !== false)
    .map(t => ({ value: t.code ?? t.value ?? t.type ?? t.id, label: t.label ?? t.nom ?? t.name ?? String(t.code ?? t.value ?? '') }))
    .filter(t => t.value != null && t.label);
  return mapped.length ? mapped : repli;
};
const statusChip = (s) => (s === 'ok' || s === 'approved' || s === 'validated' || s === 'paid'
  ? { l: 'Validé', k: 'ok' } : (s === 'refused' || s === 'rejected') ? { l: 'Refusé', k: 'bad' } : { l: 'En attente', k: 'warn' });

// Petite fabrique de listes tolérantes (leaves / frais / documents).
function useMeList(path) {
  const [items, setItems] = useState(undefined); // undefined = chargement, null = indisponible
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/resources/me/${path}`, { headers: authH() });
      if (!r.ok) { setItems(r.status === 404 || r.status === 403 ? null : []); return; }
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j[path] || (Array.isArray(j) ? j : []);
      setItems(Array.isArray(arr) ? arr : []);
    } catch { setItems(prev => (prev === undefined ? null : prev)); }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { items, load };
}

// ── Timesheet : grille CRA du mois (clés ISO, grille complète au PUT) ────────
function Timesheet({ showNotification, unit = 'jour', leaveTypes = normLeaveTypes(null), onOuvrirConges, missionTitre }) {
      // Les absences du mois : le CRA les porte quand le serveur les joint
      // (clé `conges`), sinon on les reconstruit depuis les demandes de la
      // ressource — sans quoi aucune alerte ne part et un jour couvert par une
      // maladie se saisit comme travaillé, en silence.
      const { items: mesConges } = useMeList('leaves');
      const [month, setMonth] = useState(() => mKey(new Date()));
      const [cra, setCra] = useState(null);
      const [unavailable, setUnavailable] = useState(false);
      const [busy, setBusy] = useState(false);
      // Rail « Journée sélectionnée » : le dernier jour touché se règle aussi
      // depuis le panneau de droite — même setDayValue, même sauvegarde.
      const [selDay, setSelDay] = useState(null);
      // Vue de la feuille (maquette) : Semaine / Mois / Tableau — mêmes données.
      const [vue, setVue] = useState('mois');
      const [semaine, setSemaine] = useState(null); // null = semaine courante
      const saveTimer = useRef(null);
      useEffect(() => { setSelDay(null); setSemaine(null); }, [month]);

      const load = useCallback(async () => {
        setCra(null);
        try {
          const r = await fetch(`${BASE}/resources/me/cra?month=${month}`, { headers: authH() });
          if (!r.ok) { setUnavailable(true); setCra({ days: {}, status: 'draft', locked: false }); return; }
          const j = await r.json().catch(() => ({}));
          const d = j.data || j;
          setUnavailable(false);
          // `conges` : clé OPTIONNELLE (PROMPT BACK) — les congés/absences du mois
          // posés via /resources/me/leaves, pour l'affichage seulement. Tant que
          // le serveur ne la renvoie pas, le calendrier reste comme avant.
          setCra({ days: d.days && typeof d.days === 'object' ? d.days : {}, hours: (d.hours || d.heures) && typeof (d.hours || d.heures) === 'object' ? (d.hours || d.heures) : {}, conges: d.conges && typeof d.conges === 'object' ? d.conges : {}, activites: d.activites && typeof d.activites === 'object' ? d.activites : null, lieux: d.lieux && typeof d.lieux === 'object' ? d.lieux : null, unit: craUnitFrom(d, d.cra, d.mission, { unit }), status: d.status || 'draft', locked: !!d.locked, submitted_at: d.submitted_at, validated_at: d.validated_at });
        } catch { setUnavailable(true); setCra({ days: {}, status: 'draft', locked: false }); }
      }, [month, unit]);
      useEffect(() => { load(); }, [load]);
      useEffect(() => () => clearTimeout(saveTimer.current), []);

      // Étale chaque demande sur les jours qu'elle couvre, pour ce mois.
      const congesDuMois = useMemo(() => {
        const carte = {};
        (Array.isArray(mesConges) ? mesConges : []).forEach((l) => {
          const statut = String(l.statut || l.status || '');
          if (/refus|annul|reject|cancel/i.test(statut)) return;   // une demande refusée ne couvre rien
          const d1 = String(l.date_debut || l.from || '').slice(0, 10);
          const d2 = String(l.date_fin || l.to || d1).slice(0, 10);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d1)) return;
          for (let t = new Date(`${d1}T12:00:00`); t <= new Date(`${(/^\d{4}-\d{2}-\d{2}$/.test(d2) ? d2 : d1)}T12:00:00`); t.setDate(t.getDate() + 1)) {
            const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
            if (iso.slice(0, 7) === month) carte[iso] = { type: l.type || 'conges', statut: l.statut || l.status };
          }
        });
        // Ce que dit le serveur sur le CRA prime sur notre reconstruction.
        return { ...carte, ...(cra?.conges || {}) };
      }, [mesConges, cra, month]);

      const status = cra?.status || 'draft';
      const locked = !!cra?.locked;
      // Un CRA transmis ne se retouche pas dans le dos du valideur : il faut
      // annuler la soumission d'abord. Le verrou serveur reste prioritaire.
      const editable = !locked && !unavailable && status === 'draft';
      const st = CRA_STATUS[status] || CRA_STATUS.draft;
      const activeUnit = craUnitOf(cra?.unit || unit);
          const hourly = activeUnit === 'heure';
          const entries = hourly ? (cra?.hours || {}) : (cra?.days || {});
          const stats = useMemo(() => {
            let working = 0, full = 0, half = 0, entered = 0, weekdayEntered = 0, total = 0;
            for (let d = 1; d <= monthDays(month); d += 1) {
              const weekend = isWeekend(month, d);
              if (!weekend) working += 1;
              const value = Number(entries[isoOf(month, d)] || 0);
              if (hourly) { if (value > 0) { entered += 1; total += value; if (!weekend) weekdayEntered += 1; } }
              else if (!weekend) { if (value === 1) full += 1; if (value === 0.5) half += 1; }
            }
            if (!hourly) { entered = full + half; weekdayEntered = entered; total = full + half * 0.5; }
            return { working, full, half, entered, remaining: Math.max(0, working - weekdayEntered), total };
          }, [month, entries, hourly]);

      const putCra = async (body) => {
        const r = await fetch(`${BASE}/resources/me/cra?month=${month}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(body) });
        const j = await r.json().catch(() => ({}));
        if (r.status === 423) { showNotification?.(j.message || j.error || 'Mois verrouillé.', 'error'); load(); return null; }
        // 409 : le serveur refuse une saisie posée sur une absence. Son texte
        // fait foi, et il nomme les jours en cause — on les répète, sinon
        // « conflit » ne dit pas lesquels corriger. La grille est relue pour
        // revenir à ce que le serveur a réellement gardé.
        if (r.status === 409) {
          const conflits = Array.isArray(j.conflits) ? j.conflits : [];
          const jours = conflits.map(c => {
            const q = String(c?.jour || '').slice(-2).replace(/^0/, '');
            return q ? `${q}${c?.type ? ` (${c.type})` : ''}` : null;
          }).filter(Boolean);
          showNotification?.(j.message || j.error || 'Saisie refusée : une absence est posée sur cette date.', 'error');
          if (jours.length) showNotification?.(`Jour${jours.length > 1 ? 's' : ''} en cause : ${jours.join(', ')}.`, 'error');
          load();
          return null;
        }
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
      };

      // Le serveur refuse une activité sans libellé (400) — or « + Ajouter une
      // activité » crée la ligne AVANT que l'utilisateur ait tapé, et chaque
      // frappe déclenche la sauvegarde automatique. On n'envoie donc que les
      // lignes nommées ; celles en cours de saisie restent locales à l'écran.
      const actsPourEnvoi = (grid) => {
        const propre = {};
        for (const [k, arr] of Object.entries(grid || {})) {
          if (!Array.isArray(arr)) continue;
          const nommees = arr.filter(a => String(a?.label ?? a?.libelle ?? '').trim() !== '');
          if (nommees.length) propre[k] = nommees;
        }
        return propre;
      };

      const persist = (nextDays, nextActs, nextLieux) => {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
          try {
            const body = hourly ? { hours: nextDays, unite_facturation: 'heure' } : { days: nextDays, unite_facturation: 'jour' };
            // Même règle que les activités : le lieu ne part que si le serveur
            // a annoncé la clé, jamais une écriture qu'il ignorerait en silence.
            if (nextLieux != null) body.lieux = nextLieux;
            // La grille d'activités ne part que si le serveur a annoncé la clé
            // (GET avec `activites`) — jamais d'écriture qu'il ignorerait en silence.
            if (nextActs != null) body.activites = actsPourEnvoi(nextActs);
            await putCra(body);
          }
          catch (e) { showNotification?.('Sauvegarde CRA impossible : ' + e.message, 'error'); }
        }, 600);
      };

      const setDayValue = async (d, value) => {
        if (!editable || (!hourly && isWeekend(month, d))) return;
        // Une absence posée sur la date arrête la saisie : on la nomme et on
        // demande une confirmation explicite. Un avertissement qui passe dans
        // le dos de l'utilisateur ne sert à rien — c'est ce qui laissait
        // remplir un jour de maladie comme s'il était travaillé.
        const cgAlerte = value > 0 && !Number(entries[isoOf(month, d)] || 0) ? congesDuMois[isoOf(month, d)] : null;
        if (cgAlerte) {
          const lbl = leaveTypes.find(t => t.value === cgAlerte.type)?.label || (cgAlerte.type === 'maladie' ? 'Maladie' : cgAlerte.type === 'autre' ? 'Absence' : 'Congé');
          const attente = /pending|attente/i.test(String(cgAlerte.statut || cgAlerte.status || ''));
          const quand = new Date(`${isoOf(month, d)}T12:00:00`).toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' });
          const ok = await confirmErpia({
            title: `${lbl} sur cette date`,
            message: `Le ${quand} est couvert par ${lbl.toLowerCase()}${attente ? ' — demande en attente' : ' validé'}.`,
            detail: 'Le saisir comme travaillé crée une incohérence, signalée à votre entreprise avant la validation du mois.',
            confirmLabel: 'Saisir quand même',
            tone: 'danger',
          });
          if (!ok) return;
        }
        setCra(prev => {
          const nd = { ...(hourly ? prev?.hours : prev?.days) };
          const key = isoOf(month, d);
          if (!value) delete nd[key]; else nd[key] = value;
          persist(nd, prev?.activites ?? null, prev?.lieux ?? null);
          return hourly ? { ...prev, hours: nd } : { ...prev, days: nd };
        });
      };

      // Un clic fait tourner le jour : 1 jour → ½ jour → non travaillé.
      const cycle = (d) => {
        const current = Number(entries[isoOf(month, d)] || 0);
        return setDayValue(d, current === 1 ? 0.5 : current === 0.5 ? 0 : 1);
      };

      const fillMonth = () => {
        if (!editable) return;
        const nd = { ...entries };
        // Un jour couvert par un congé ou une absence (validé OU en attente)
        // ne se remplit pas : le remplissage automatique respecte les demandes.
        let sautes = 0;
        for (let d = 1; d <= monthDays(month); d += 1) {
          if (isWeekend(month, d)) continue;
          if (congesDuMois[isoOf(month, d)]) { sautes += 1; continue; }
          nd[isoOf(month, d)] = hourly ? 8 : 1;
        }
        setCra(prev => { persist(nd, prev?.activites ?? null, prev?.lieux ?? null); return hourly ? ({ ...prev, hours: nd }) : ({ ...prev, days: nd }); });
        const detail = sautes ? ` ${sautes} jour${sautes > 1 ? 's' : ''} en congé ou absence n'${sautes > 1 ? 'ont' : 'a'} pas été rempli${sautes > 1 ? 's' : ''}.` : '';
        showNotification?.((hourly ? 'Les jours ouvrés sont préremplis à 8 heures — ajustez puis soumettez.' : 'Les jours ouvrés sont marqués à 1 jour — vérifiez puis soumettez.') + detail, 'success');
      };

      const clearMonth = () => {
        if (!editable || stats.entered === 0) return;
        setCra(prev => { persist({}, prev?.activites ?? null, prev?.lieux ?? null); return hourly ? ({ ...prev, hours: {} }) : ({ ...prev, days: {} }); });
        showNotification?.('Saisie du mois effacée.', 'success');
      };

      // Reprendre la main sur un CRA transmis mais pas encore validé.
      const [annulation, setAnnulation] = useState(false);
      const annulerSoumission = async () => {
        const ok = await confirmErpia({
          title: 'Annuler la soumission',
          message: 'Votre CRA repassera en saisie et ne sera plus en attente de validation.',
          detail: 'Vous devrez le transmettre à nouveau une fois vos corrections faites.',
          confirmLabel: 'Annuler la soumission',
          tone: 'danger',
        });
        if (!ok) return;
        setAnnulation(true);
        try {
          const r = await fetch(`${BASE}/resources/me/cra?month=${month}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ submit: false }) });
          const j = await r.json().catch(() => ({}));
          if ([404, 405, 501].includes(r.status) && !j.error) {
            showNotification?.("L'annulation de soumission arrive côté serveur — votre CRA reste transmis.", 'info');
            return;
          }
          if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
          showNotification?.(j.message || 'Soumission annulée — votre CRA est de nouveau modifiable.', 'success');
          load();
        } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
        finally { setAnnulation(false); }
      };

      const submit = async () => {
        setBusy(true);
        try {
          clearTimeout(saveTimer.current);
          const j = await putCra({ ...(hourly ? { hours: entries, unite_facturation: 'heure' } : { days: entries, unite_facturation: 'jour' }), ...(cra?.activites != null ? { activites: actsPourEnvoi(cra.activites) } : {}), submit: true });
          if (j) { showNotification?.(j.message || 'CRA soumis au client pour validation.', 'success'); load(); }
        } catch (e) { showNotification?.('Erreur CRA : ' + e.message, 'error'); }
        finally { setBusy(false); }
      };

      const pads = firstDayPad(month);
      const nDays = monthDays(month);
      const progress = stats.working ? Math.min(100, Math.round((stats.entered / stats.working) * 100)) : 0;
      const jourLabel = (d) => { const [y, m] = month.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('fr-CH', { weekday: 'long' }); };
      const moisCourt = (() => { const [, m] = month.split('-').map(Number); return MONTHS[m - 1]; })();
      const selValue = selDay != null ? Number(entries[isoOf(month, selDay)] || 0) : 0;

      // Congés du mois (clé serveur optionnelle) — affichage seulement : la
      // saisie reste possible, le serveur reste seul juge des conflits.
      const conges = congesDuMois;
      const aDesConges = Object.keys(conges).length > 0;
      const congeOf = (d) => conges[isoOf(month, d)] || null;
      const congeLabel = (cg) => leaveTypes.find(t => t.value === cg?.type)?.label || (cg?.type === 'maladie' ? 'Maladie' : cg?.type === 'autre' ? 'Absence' : 'Congé');
      const congeEnAttente = (cg) => /pending|attente/i.test(String(cg?.statut || cg?.status || ''));

      // Activités par journée — clé serveur OPTIONNELLE `activites`
      // ({ 'YYYY-MM-DD': [ { label, heures } ] }). Sa présence dans le GET
      // (même vide) signale que le serveur les persiste : l'édition s'active.
      const actSupport = cra?.activites != null;
      // Le lieu d'exécution : présentiel par défaut, télétravail quand la date
      // est marquée. La clé n'est écrite que pour le télétravail — l'absence de
      // marque vaut « sur site », ce qui garde les mois existants inchangés.
      const lieuSupport = cra?.lieux != null;
      const lieuDe = (d) => ((cra?.lieux || {})[isoOf(month, d)] === 'teletravail' ? 'teletravail' : 'presentiel');
      const setDayLieu = (d, lieu) => {
        if (!editable || !lieuSupport) return;
        setCra(prev => {
          const nl = { ...(prev?.lieux || {}) };
          const key = isoOf(month, d);
          if (lieu === 'teletravail') nl[key] = 'teletravail'; else delete nl[key];
          persist(hourly ? (prev?.hours || {}) : (prev?.days || {}), prev?.activites ?? null, nl);
          return { ...prev, lieux: nl };
        });
      };
      // Un jour non travaillé n'a pas de lieu : on nettoie en même temps.
      const setJournee = async (d, value, lieu) => {
        await setDayValue(d, value);
        if (lieuSupport) setDayLieu(d, value > 0 ? lieu : 'presentiel');
      };
      const activites = cra?.activites || {};
      const activitesOf = (d) => { const a = activites[isoOf(month, d)]; return Array.isArray(a) ? a.map(x => ({ label: x.label ?? x.libelle ?? '', heures: Number(x.heures ?? x.hours ?? 0) || 0 })) : []; };
      const totalActs = (arr) => arr.reduce((somme, a) => somme + (Number(a.heures) || 0), 0);
      const setActivitesJour = (d, arr) => {
        if (!editable || !actSupport) return;
        setCra(prev => {
          const na = { ...(prev?.activites || {}) };
          const key = isoOf(month, d);
          if (!arr || arr.length === 0) delete na[key]; else na[key] = arr;
          persist(hourly ? (prev?.hours || {}) : (prev?.days || {}), na);
          return { ...prev, activites: na };
        });
      };

      const jourCourt = (d) => { const [y, m] = month.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('fr-CH', { weekday: 'short' }); };

      // Cellule du calendrier : le liseré HAUT porte l'état (accent = travaillé,
      // ambre = congé, pointillé = à saisir, éteint = week-end) — maquette.
      const cellStyle = (d) => {
        const weekend = isWeekend(month, d);
        const value = Number(entries[isoOf(month, d)] || 0);
        const conge = !weekend && !value && congeOf(d);
        const sel = selDay === d;
        const base = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px', padding: '6px 7px', borderRadius: '6px', border: '1px solid var(--hair)', borderTop: '3px solid var(--hair)', background: 'var(--code-bg)', color: 'var(--text-3)', textAlign: 'left', userSelect: 'none', fontFamily: 'inherit', transition: 'border-color .12s ease, background .12s ease', opacity: locked && !weekend ? 0.62 : 1, ...(sel ? { boxShadow: 'inset 0 0 0 1px var(--accent)', borderColor: 'var(--accent)' } : null) };
        if (weekend && !hourly) return { ...base, background: 'transparent', color: 'var(--text-faint)', cursor: 'default' };
        if (hourly) return { ...base, borderTop: value > 0 ? '3px solid var(--accent)' : '3px solid var(--hair)', background: value > 0 ? 'var(--accent-soft-bg)' : 'var(--code-bg)', color: 'var(--text-strong)', cursor: 'pointer' };
        if (value === 1) return { ...base, borderTop: '3px solid var(--accent)', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', cursor: editable ? 'pointer' : 'default' };
        if (value === 0.5) return { ...base, borderTop: '3px solid var(--accent)', background: 'var(--surface)', color: 'var(--text-strong)', cursor: editable ? 'pointer' : 'default' };
        if (conge) return { ...base, borderTop: `3px solid var(--erp-warning)`, background: congeEnAttente(conge) ? 'var(--code-bg)' : 'var(--erp-warning-soft, var(--code-bg))', color: 'var(--text-strong)', cursor: editable ? 'pointer' : 'default' };
        return { ...base, border: '1px dashed var(--input-border)', borderTop: '3px solid var(--hair)', background: 'transparent', cursor: editable ? 'pointer' : 'default' };
      };

      // Découpage du mois en semaines calendaires (lundi en tête) pour la vue
      // Semaine ; la semaine courante est présélectionnée sur le mois en cours.
      const weeks = [];
      for (let d = 1; d <= nDays; d += 1) { const w = Math.floor((pads + d - 1) / 7); (weeks[w] = weeks[w] || []).push(d); }
      const semaineDefaut = (() => { const now = new Date(); return mKey(now) === month ? Math.floor((pads + now.getDate() - 1) / 7) : 0; })();
      const semaineIdx = Math.max(0, Math.min(semaine ?? semaineDefaut, weeks.length - 1));

      // Ligne d'une journée (vues Semaine et Tableau) — le clic sélectionne la
      // journée, le réglage se fait dans le rail « Journée sélectionnée ».
      const LigneJour = ({ d }) => {
        const weekend = isWeekend(month, d);
        const value = Number(entries[isoOf(month, d)] || 0);
        const cg = !weekend && !value ? congeOf(d) : null;
        const acts = activitesOf(d);
        const sel = selDay === d;
        return (
          <button type="button" onClick={() => setSelDay(d)} disabled={weekend && !hourly}
            style={{ display: 'grid', gridTemplateColumns: '54px 44px minmax(0,1fr) 78px', gap: '10px', alignItems: 'center', width: '100%', padding: '9px 8px', border: 'none', borderTop: '1px solid var(--hair)', background: sel ? 'var(--accent-soft-bg)' : 'transparent', boxShadow: sel ? 'inset 2px 0 0 var(--accent)' : 'none', textAlign: 'left', fontFamily: 'inherit', cursor: weekend && !hourly ? 'default' : 'pointer', opacity: weekend ? 0.55 : 1, color: 'var(--text)' }}>
            <span className="ats-mono" style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-strong)' }}>{String(d).padStart(2, '0')}.{month.slice(5)}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--text-2)', textTransform: 'capitalize' }}>{jourCourt(d)}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {weekend ? 'Repos'
                : acts.length ? (acts.map(a => a.label).filter(Boolean).join(' · ') || 'Travail')
                : cg ? `${congeLabel(cg)}${congeEnAttente(cg) ? ' (en attente)' : ''}`
                : value > 0 ? (missionTitre || 'Travail') : 'À saisir'}
            </span>
            <span className="ats-mono" style={{ fontSize: '11.5px', fontWeight: 700, textAlign: 'right', color: value > 0 ? 'var(--text-strong)' : cg ? 'var(--erp-warning)' : 'var(--text-3)' }}>
              {weekend ? '—' : hourly ? (value > 0 ? `${String(value).replace('.', ',')} h` : '—') : value === 1 ? '1 j' : value === 0.5 ? '½ j' : cg ? congeLabel(cg).slice(0, 1).toUpperCase() : '—'}
            </span>
          </button>
        );
      };
      const enTeteLignes = (
        <div style={{ display: 'grid', gridTemplateColumns: '54px 44px minmax(0,1fr) 78px', gap: '10px', padding: '2px 8px 7px' }}>
          {['Date', 'Jour', 'Activité', hourly ? 'Heures' : 'Jours'].map((l, i) => <span key={l} style={{ ...LBL, fontSize: '9.5px', textAlign: i === 3 ? 'right' : 'left' }}>{l}</span>)}
        </div>
      );

      const actsSel = selDay != null ? activitesOf(selDay) : [];
      const majActSel = (i, patch) => setActivitesJour(selDay, actsSel.map((a, j) => (j === i ? { ...a, ...patch } : a)));

      return (<>
        {/* Avancement du mois — libellé, piste, échéance, statut serveur */}
        <div className="cra-avancement" style={{ ...CARD, display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>Avancement du mois</span>
            <b style={{ fontSize: '12px', color: 'var(--text-strong)' }}>{hourly ? `${String(stats.total).replace('.', ',')} h saisies` : `${stats.entered} / ${stats.working} jours saisis`}</b>
          </div>
          <div style={{ flex: '1 1 140px', height: '5px', borderRadius: '4px', background: 'var(--code-bg)', overflow: 'hidden' }}><div style={{ height: '100%', width: progress + '%', borderRadius: 'inherit', background: 'var(--accent)', transition: 'width .2s ease' }} /></div>
          {editable && status === 'draft' && <span style={{ fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>À transmettre avant le {nDays} {moisCourt}</span>}
          <Chip kind={st.k} title={cra?.validated_at ? 'Validé le ' + fmtDate(cra.validated_at) : cra?.submitted_at ? 'Soumis le ' + fmtDate(cra.submitted_at) : undefined}><BI icon={locked ? 'lock-fill' : status === 'submitted' ? 'send-check' : 'pencil'} style={{ fontSize: '10px' }} /> {st.l}</Chip>
        </div>

        <div className="cra-layout">
          {/* Feuille de saisie */}
          <section style={{ ...CARD, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '17px 18px 14px', flexWrap: 'wrap' }}>
              <div>
                <div style={LBL}>Saisie des journées</div>
                <h2 style={{ margin: '4px 0 0', fontFamily: "'Archivo',sans-serif", fontSize: '19px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}><span style={{ textTransform: 'capitalize' }}>{vue === 'semaine' ? `Semaine ${semaineIdx + 1}` : mLabel(month)}</span> <span style={{ fontFamily: "'IBM Plex Sans',sans-serif", fontSize: '11.5px', fontWeight: 500, letterSpacing: 0, color: 'var(--text-3)' }}>{vue === 'semaine' ? `· ${weeks[semaineIdx][0]} — ${weeks[semaineIdx][weeks[semaineIdx].length - 1]} ${moisCourt}` : `· ${stats.working} jours ouvrés · ${hourly ? 'saisie à l’heure' : 'saisie au jour'}`}</span></h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <div role="tablist" aria-label="Vue du CRA" style={{ display: 'flex', padding: '2px', border: '1px solid var(--border)', borderRadius: '7px', background: 'var(--code-bg)', flex: 'none' }}>
                  {[['semaine', 'Semaine'], ['mois', 'Mois'], ['tableau', 'Tableau']].map(([v, l]) => (
                    <button key={v} role="tab" aria-selected={vue === v} onClick={() => setVue(v)}
                      style={{ border: 'none', borderRadius: '5px', padding: '4px 10px', fontSize: '11.5px', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', background: vue === v ? 'var(--surface)' : 'transparent', color: vue === v ? 'var(--text-strong)' : 'var(--text-3)', boxShadow: vue === v ? '0 1px 3px var(--hover-shadow)' : 'none' }}>{l}</button>
                  ))}
                </div>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Mois précédent"><BI icon="chevron-left" /></button>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setMonth(mKey(new Date()))}>Aujourd’hui</button>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Mois suivant"><BI icon="chevron-right" /></button>
                {editable && <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={fillMonth}><BI icon="clock" /> Remplir tout le mois</button>}
                {editable && <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={clearMonth} disabled={!stats.entered}><BI icon="eraser" /> Effacer</button>}
              </div>
            </div>

            {unavailable && <div style={{ padding: '11px 18px', borderTop: '1px solid var(--hair)', fontSize: '12.5px', color: 'var(--text-2)', background: 'var(--code-bg)' }}><BI icon="plug" style={{ marginRight: '7px' }} />CRA indisponible pour ce mois — rien ne sera enregistré.</div>}

            {cra === null ? <div style={{ padding: '42px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px', borderTop: '1px solid var(--hair)' }}><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite', marginRight: '7px' }} />Chargement de votre CRA…</div> : (
              <div style={{ padding: '13px 18px 18px', borderTop: '1px solid var(--hair)' }}>
                {vue === 'mois' && (<>
                  {/* Légende + geste de saisie */}
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 14px', marginBottom: '12px', fontSize: '11px', color: 'var(--text-3)' }}>
                    {(hourly
                      ? [['plein', 'Heures saisies'], ['vide', 'À saisir']]
                      : [['plein', '1 jour'], ['demi', '½ jour'], ...(lieuSupport ? [['tt', 'Télétravail']] : []), ...(aDesConges ? [['conge', 'Congé']] : []), ['vide', 'À saisir'], ['repos', 'Week-end']]
                    ).map(([k, l]) => (
                      <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '9px', height: '9px', borderRadius: '2px', flex: 'none',
                          background: k === 'plein' ? 'var(--accent)' : k === 'demi' ? 'var(--accent-soft-bg)' : k === 'conge' ? 'var(--erp-warning-soft, transparent)' : 'transparent',
                          border: k === 'plein' ? 'none' : k === 'demi' ? '1px solid var(--accent)' : k === 'tt' ? '2px solid var(--accent)' : k === 'conge' ? '1px solid var(--erp-warning)' : k === 'vide' ? '1px dashed var(--input-border)' : '1px solid var(--hair)',
                          ...(k === 'tt' ? { borderRadius: '50%' } : null) }} />{l}
                      </span>
                    ))}
                    <span style={{ marginLeft: 'auto' }}>{editable ? (hourly ? 'Saisissez les heures dans chaque case.' : 'Un clic fait tourner : 1 jour → ½ jour → vide.') : locked ? 'Lecture seule' : ''}</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: '5px', marginBottom: '5px' }}>{WD.map(w => <span key={w} style={{ padding: '0 2px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', fontWeight: 700, color: 'var(--text-faint)' }}>{w}</span>)}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: '5px' }}>
                    {Array.from({ length: pads }, (_, i) => <div key={'p' + i} />)}
                    {Array.from({ length: nDays }, (_, i) => {
                      const d = i + 1;
                      const weekend = isWeekend(month, d);
                      const value = Number(entries[isoOf(month, d)] || 0);
                      const valueJour = Number(entries[isoOf(month, d)] || 0);
                      const actsSrv = weekend ? [] : activitesOf(d);
                      const acts = actsSrv.length > 0 ? actsSrv : (!weekend && valueJour > 0 && missionTitre ? [{ label: missionTitre, heures: 0 }] : []);
                      const ligneAct = acts.length > 0 && (
                        <span className="cra-day-etat" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '9px', lineHeight: 1.3, color: 'var(--text-3)', borderLeft: '2px solid var(--accent)', paddingLeft: '4px' }}>{acts[0].label || '—'}{acts.length > 1 ? ` +${acts.length - 1}` : ''}</span>
                      );
                      if (hourly) return <div key={d} className="cra-day" style={cellStyle(d)} onClick={() => setSelDay(d)}><span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', fontWeight: 700, opacity: 0.75 }}>{d}</span>{ligneAct}<div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><input type="number" min="0" max="24" step="0.25" value={value || ''} onChange={(event) => setDayValue(d, Math.max(0, Math.min(24, Number(event.target.value) || 0)))} disabled={!editable} aria-label={'Heures travaillées le ' + d} placeholder="0" style={{ width: '40px', padding: '3px 3px', border: '1px solid var(--input-border)', borderRadius: '5px', background: 'var(--input-bg)', color: 'var(--text-strong)', textAlign: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', outline: 'none' }} /><span style={{ fontSize: '10px', color: 'var(--text-3)' }}>h</span></div></div>;
                      const cg = !weekend && !value ? congeOf(d) : null;
                      const tt = lieuSupport && value > 0 && lieuDe(d) === 'teletravail';
                      const etat = weekend ? 'Repos' : value === 1 ? '1 j' : value === 0.5 ? '½ j' : cg ? congeLabel(cg) + (congeEnAttente(cg) ? ' ?' : '') : 'À saisir';
                      return <button key={d} type="button" className="cra-day" data-teletravail={tt ? 'true' : undefined} disabled={!editable || weekend} onClick={() => { setSelDay(d); cycle(d); }} aria-label={'Jour ' + d + (weekend ? ' — week-end' : value === 1 ? ' — 1 jour' : value === 0.5 ? ' — demi-journée' : cg ? ` — ${congeLabel(cg).toLowerCase()}` : ' — non saisi') + (tt ? ' — en télétravail' : '')} title={weekend ? 'Week-end' : cg ? `${congeLabel(cg)}${congeEnAttente(cg) ? ' — demande en attente' : ' validé'} · un clic saisit quand même la journée` : tt ? 'Journée en télétravail — le lieu se change dans « Journée sélectionnée »' : 'Cliquer pour changer la saisie'} style={cellStyle(d)}><span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', fontWeight: 700, opacity: 0.75 }}>{d}</span>{ligneAct}<span className="cra-day-etat" style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', fontWeight: 700, ...(cg ? { color: 'var(--erp-warning)' } : null) }}>{tt ? `${etat} · TT` : etat}</span></button>;
                    })}
                  </div>
                </>)}

                {vue === 'semaine' && (<>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {weeks.map((ws, i) => (
                      <button key={i} className={`erp-btn erp-btn--sm ${i === semaineIdx ? 'erp-btn--primary' : 'erp-btn--secondary'}`} onClick={() => setSemaine(i)}>
                        S{i + 1} <span style={{ fontWeight: 400, opacity: 0.8 }}>· {ws[0]}–{ws[ws.length - 1]}</span>
                      </button>
                    ))}
                  </div>
                  {enTeteLignes}
                  <div>{weeks[semaineIdx].filter(d => hourly || !isWeekend(month, d)).map(d => <LigneJour key={d} d={d} />)}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '12px', fontSize: '11px', color: 'var(--text-3)' }}>
                    <span>Le réglage d'une journée se fait dans le panneau « Journée sélectionnée ».</span>
                    <b className="ats-mono" style={{ color: 'var(--text-strong)' }}>{(() => { const t = weeks[semaineIdx].reduce((somme, d) => somme + Number(entries[isoOf(month, d)] || 0), 0); return `${String(t).replace('.', ',')} ${hourly ? 'h' : 'j'}`; })()}</b>
                  </div>
                </>)}

                {vue === 'tableau' && (<>
                  {enTeteLignes}
                  <div>{Array.from({ length: nDays }, (_, i) => i + 1).map(d => <LigneJour key={d} d={d} />)}</div>
                </>)}

                {/* Pied d'actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '17px', paddingTop: '14px', borderTop: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                  {locked
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: 'var(--text-2)' }}><BI icon="lock-fill" />Ce CRA est validé par le client et ne peut plus être modifié.</span>
                    : status === 'submitted' ? <>
                      {/* Transmis : la grille est figée. Pour corriger, on
                          reprend d'abord la main — c'est le seul chemin. */}
                      <span style={{ flex: 1, minWidth: '160px', display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                        <BI icon="send-check" />Transmis pour validation — plus modifiable. Annulez la soumission pour corriger.
                      </span>
                      <button className="erp-btn erp-btn--secondary" onClick={annulerSoumission} disabled={annulation}>
                        <BI icon={annulation ? 'arrow-repeat' : 'arrow-counterclockwise'} style={annulation ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Annuler la soumission
                      </button>
                    </> : editable ? <>
                      <span style={{ flex: 1, minWidth: '160px', fontSize: '11.5px', color: 'var(--text-3)' }}>Vérifiez le récapitulatif avant de transmettre.</span>
                      <button className="erp-btn erp-btn--primary" onClick={submit} disabled={busy || stats.total === 0}><BI icon={busy ? 'arrow-repeat' : 'send'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Soumettre le CRA</button>
                    </> : null}
                </div>
              </div>
            )}
          </section>

          {/* Rail — journée sélectionnée + récapitulatif (maquette) */}
          <aside style={{ display: 'grid', gap: '16px' }}>
            <div style={{ ...CARD, padding: '16px 17px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                <div style={LBL}>Journée sélectionnée</div>
                <BI icon="calendar3" style={{ color: 'var(--text-faint)', fontSize: '13px' }} />
              </div>
              {selDay == null ? (
                <p style={{ margin: '9px 0 0', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text-3)' }}>{hourly ? 'Touchez une date du calendrier pour saisir ses heures ici — ou directement dans la case.' : 'Touchez une date pour la régler ici — état de la journée et activités.'}</p>
              ) : (<>
                <h2 style={{ margin: '6px 0 0', fontFamily: "'Archivo',sans-serif", fontSize: '17px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}><span style={{ textTransform: 'capitalize' }}>{jourLabel(selDay)}</span> {selDay} {moisCourt}</h2>

                {hourly ? (
                  <div style={{ marginTop: '13px' }}>
                    <div style={{ ...LBL, fontSize: '9.5px' }}>Heures de la journée</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                      <input className="erp-field" type="number" min="0" max="24" step="0.25" value={selValue || ''} placeholder="0" disabled={!editable} onChange={(event) => setDayValue(selDay, Math.max(0, Math.min(24, Number(event.target.value) || 0)))} aria-label="Heures de la journée sélectionnée" style={{ width: '92px', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace" }} />
                      <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>heures travaillées</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '13px' }}>
                    <div style={{ ...LBL, fontSize: '9.5px' }}>État de la journée</div>
                    {/* Quantité ET lieu dans une seule liste : c'est une seule
                        décision pour la personne qui saisit — « qu'est-ce que
                        j'ai fait ce jour-là ». */}
                    <ErpiaSelect
                      value={selValue === 0 ? '0' : `${selValue}:${selDay != null ? lieuDe(selDay) : 'presentiel'}`}
                      disabled={!editable}
                      onChange={(e) => { const [v, l] = String(e.target.value).split(':'); setJournee(selDay, Number(v), l || 'presentiel'); }}
                      style={{ ...INPUT, width: '100%', marginTop: '6px', padding: '8px 10px', fontSize: '12.5px' }} aria-label="État de la journée sélectionnée">
                      <option value="1:presentiel">Travail — journée complète sur site</option>
                      <option value="1:teletravail" disabled={!lieuSupport}>Travail — journée complète en télétravail</option>
                      <option value="0.5:presentiel">Travail — demi-journée sur site</option>
                      <option value="0.5:teletravail" disabled={!lieuSupport}>Travail — demi-journée en télétravail</option>
                      <option value="0">Non travaillé</option>
                    </ErpiaSelect>
                    {!lieuSupport && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-3)' }}>
                        <BI icon="hourglass-split" style={{ marginRight: '5px' }} />Le télétravail arrive côté serveur — vos journées restent comptées sur site d'ici là.
                      </div>
                    )}
                  </div>
                )}

                {(() => { const cg = congeOf(selDay); return cg ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginTop: '11px', padding: '9px 10px', borderRadius: '7px', border: '1px solid var(--erp-warning)', background: 'var(--erp-warning-soft, var(--code-bg))', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--text-2)' }}>
                    <BI icon="sun" style={{ marginTop: '1px', color: 'var(--erp-warning)' }} />
                    <span>{congeLabel(cg)}{congeEnAttente(cg) ? ' — demande en attente de validation.' : ' validé sur cette journée.'}</span>
                  </div>
                ) : null; })()}

                {/* Activités de la journée — écriture uniquement quand le serveur
                    porte la clé `activites` ; sinon les champs sont annoncés. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '14px' }}>
                  <span style={{ ...LBL, fontSize: '9.5px' }}>Activités de la journée</span>
                  {actSupport && actsSel.length > 0 && <b className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-strong)' }}>{String(totalActs(actsSel)).replace('.', ',')} h</b>}
                </div>
                {actSupport ? (<>
                  <div style={{ display: 'grid', gap: '6px', marginTop: '7px' }}>
                    {actsSel.length === 0 && <p style={{ margin: 0, fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-faint)' }}>{missionTitre ? <>Par défaut : <b style={{ color: 'var(--text-2)' }}>{missionTitre}</b> — détaillez si la journée s'est partagée.</> : 'Aucune activité décrite pour cette journée.'}</p>}
                    {actsSel.map((a, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 62px 26px', gap: '5px', alignItems: 'center' }}>
                        <input className="erp-field" value={a.label} placeholder="Activité — ex. Développement" disabled={!editable}
                          onChange={e => majActSel(i, { label: e.target.value })} aria-label={`Libellé de l'activité ${i + 1}`}
                          style={{ minHeight: '30px', padding: '4px 8px', fontSize: '12px' }} />
                        <input className="erp-field ats-mono" type="number" min="0" max="24" step="0.25" value={a.heures || ''} placeholder="0" disabled={!editable}
                          onChange={e => majActSel(i, { heures: Math.max(0, Math.min(24, Number(e.target.value) || 0)) })} aria-label={`Heures de l'activité ${i + 1}`}
                          style={{ minHeight: '30px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
                        <button disabled={!editable} onClick={() => setActivitesJour(selDay, actsSel.filter((_, j) => j !== i))} aria-label={`Supprimer l'activité ${i + 1}`}
                          style={{ width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '5px', background: 'none', color: 'var(--text-3)', cursor: editable ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}><BI icon="x" /></button>
                      </div>
                    ))}
                  </div>
                  <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={!editable} onClick={() => setActivitesJour(selDay, [...actsSel, { label: actsSel.length === 0 ? (missionTitre || '') : '', heures: 0 }])} style={{ marginTop: '7px', width: '100%', justifyContent: 'flex-start' }}>+ Ajouter une activité</button>
                </>) : (
                  <p style={{ margin: '7px 0 0', fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-faint)' }}>Le détail des activités (libellé + heures) arrive côté serveur — en attendant, la journée se règle avec l'état ci-dessus.</p>
                )}
              </>)}

              {onOuvrirConges && <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={onOuvrirConges} style={{ marginTop: '11px', width: '100%', justifyContent: 'flex-start' }}><BI icon="sun" /> Déclarer un congé</button>}
              {editable && selDay != null && <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '13px', paddingTop: '11px', borderTop: '1px solid var(--hair)', fontSize: '11.5px', color: 'var(--text-2)' }}><BI icon="check2" /> Enregistré automatiquement</div>}
            </div>

            <div style={{ ...CARD, padding: '16px 17px' }}>
              <div style={{ paddingBottom: '12px', borderBottom: '1px solid var(--hair)' }}>
                <div style={LBL}>Récapitulatif</div>
                <h2 style={{ margin: '4px 0 0', fontFamily: "'Archivo',sans-serif", fontSize: '16px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', textTransform: 'capitalize' }}>{mLabel(month)}</h2>
              </div>
              <div style={{ padding: '13px 0 14px' }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '27px', fontWeight: 700, letterSpacing: '-0.04em', color: 'var(--text-strong)' }}>{String(stats.total).replace('.', ',')} <span style={{ fontSize: '15px', color: 'var(--text-3)' }}>{hourly ? 'h' : 'j'}</span></div>
                <div style={{ marginTop: '2px', fontSize: '10.5px', color: 'var(--text-3)' }}>{hourly ? 'heures déclarées' : 'jours déclarés'}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid var(--hair)', borderLeft: '1px solid var(--hair)' }}>
                {(hourly
                  ? [[String(stats.entered), 'jours renseignés'], [String(stats.remaining), 'jours à compléter'], [String(stats.working), 'jours ouvrés'], ['Heure', 'unité de facturation']]
                  : [[String(stats.full), 'journées complètes'], [String(stats.half), 'demi-journées'], [String(stats.remaining), 'jours à compléter'], [String(stats.working), 'jours ouvrés']]
                ).map(([v, l]) => (
                  <div key={l} style={{ padding: '10px 10px 9px', borderRight: '1px solid var(--hair)', borderBottom: '1px solid var(--hair)' }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>{v}</div>
                    <div style={{ marginTop: '2px', fontSize: '9.5px', lineHeight: 1.3, color: 'var(--text-3)' }}>{l}</div>
                  </div>
                ))}
              </div>
              {(cra?.validated_at || cra?.submitted_at) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginTop: '13px', fontSize: '11px', lineHeight: 1.45, color: 'var(--text-3)' }}>
                  <BI icon={locked ? 'lock-fill' : 'send-check'} style={{ marginTop: '1px' }} />
                  <span>{cra?.validated_at ? <>Validé par le client<br /><b style={{ color: 'var(--text-2)' }}>{fmtDate(cra.validated_at)}</b></> : <>Transmis pour validation<br /><b style={{ color: 'var(--text-2)' }}>{fmtDate(cra.submitted_at)}</b></>}</span>
                </div>
              )}
            </div>
          </aside>
        </div>
      </>);
    }

    // ── Congés : solde + demandes + calendrier de période ────────────────────────
function Conges({ showNotification, leaveTypes = normLeaveTypes(null), solde }) {
  const { items: leaves, load: reloadLeaves } = useMeList('leaves');
  const [calMonth, setCalMonth] = useState(() => mKey(new Date()));
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [type, setType] = useState('conges');
  const [motif, setMotif] = useState('');
  const [sending, setSending] = useState(false);

  const dayKey = (d) => isoOf(calMonth, d);
  const pick = (d) => {
    if (isWeekend(calMonth, d)) return;
    const k = dayKey(d);
    if (!selStart || (selStart && selEnd)) { setSelStart(k); setSelEnd(null); }
    else if (k < selStart) { setSelStart(k); }
    else setSelEnd(k);
  };
  const inRange = (d) => { const k = dayKey(d); return selStart && selEnd && k >= selStart && k <= selEnd; };

  // Sens inverse de l'alerte du CRA : si la période demandée chevauche des
  // jours DÉJÀ SAISIS comme travaillés, on le dit avant l'envoi. Vérification
  // silencieuse (relit le CRA des mois couverts) — sans réponse, pas d'alerte
  // inventée ; la demande reste possible, le serveur tranche à la validation.
  const [saisisChevauche, setSaisisChevauche] = useState(0);
  useEffect(() => {
    let annule = false;
    setSaisisChevauche(0);
    if (!selStart || !selEnd) return undefined;
    (async () => {
      try {
        const mois = [];
        let cur = selStart.slice(0, 7);
        const fin = selEnd.slice(0, 7);
        while (cur <= fin && mois.length < 14) { mois.push(cur); cur = shiftMonth(cur, 1); }
        let n = 0;
        for (const m of mois) {
          const r = await fetch(`${BASE}/resources/me/cra?month=${m}`, { headers: authH() });
          if (!r.ok) return;
          const j = await r.json().catch(() => ({}));
          const d = j.data || j;
          const grille = { ...(d.days || {}), ...(d.hours || d.heures || {}) };
          for (const [k, v] of Object.entries(grille)) { if (Number(v) > 0 && k >= selStart && k <= selEnd) n += 1; }
        }
        if (!annule) setSaisisChevauche(n);
      } catch { /* vérification silencieuse */ }
    })();
    return () => { annule = true; };
  }, [selStart, selEnd]);

  const workDays = useMemo(() => {
    if (!selStart || !selEnd) return 0;
    let n = 0; const a = new Date(selStart), b = new Date(selEnd);
    for (let t = new Date(a); t <= b; t.setDate(t.getDate() + 1)) { const w = t.getDay(); if (w !== 0 && w !== 6) n++; }
    return n;
  }, [selStart, selEnd]);


  const submit = async () => {
    if (!selStart || !selEnd) return;
    if (joursDejaPoses > 0) { showNotification?.(`${joursDejaPoses} jour(s) de cette période ${joursDejaPoses > 1 ? 'sont' : 'est'} déjà couvert(s) par une demande de congé — ajustez les dates avant d'envoyer.`, 'error'); return; }
    setSending(true);
    try {
      const body = { date_debut: selStart, date_fin: selEnd, type };
      if (motif.trim()) body.motif = motif.trim();
      const r = await fetch(`${BASE}/resources/me/leaves`, { method: 'POST', headers: jsonH(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || `Demande envoyée — ${workDays} jour(s) ouvré(s).`, 'success');
      setSelStart(null); setSelEnd(null); setMotif('');
      reloadLeaves();
    } catch (e) { showNotification?.('Erreur demande de congé : ' + e.message, 'error'); }
    finally { setSending(false); }
  };

  const pads = firstDayPad(calMonth);
  const nDays = monthDays(calMonth);
  const typeLabel = (t) => leaveTypes.find(x => x.value === t)?.label || (LEAVE_TYPES.find(([k]) => k === t) || [null, t])[1];

  // Annuler une demande EN COURS (jamais une validée : elle appartient au flux
  // de validation). Tant que la route n'existe pas, on l'annonce sans rien
  // écrire — le contrat est proposé au back dans la PR.
  const [annulationId, setAnnulationId] = useState(null);
  const annuler = async (l) => {
    if (l.id == null) return;
    const lbl = typeLabel(l.type) || 'Congés';
    if (!await confirmErpia({ message: `Annuler la demande « ${lbl} » du ${fmtDate(l.date_debut || l.from)} au ${fmtDate(l.date_fin || l.to)} ?\n\nElle disparaîtra de la liste et les dates redeviendront disponibles.`, confirmLabel: 'Annuler la demande', tone: 'danger' })) return;
    setAnnulationId(l.id);
    try {
      const r = await fetch(`${BASE}/resources/me/leaves/${l.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      // Contrat serveur : 409 approuvée/refusée et 404 introuvable portent un
      // message affichable tel quel ; un 404/405/501 SANS corps d'erreur reste
      // le signe d'une route pas encore servie.
      if ((r.status === 404 || r.status === 405 || r.status === 501) && !j.error && !j.message) {
        showNotification?.('L\'annulation des demandes arrive côté serveur — la demande n\'a pas été touchée.', 'error'); return;
      }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || 'Demande annulée.', 'success');
      reloadLeaves();
    } catch (e) { showNotification?.('Annulation impossible : ' + e.message, 'error'); }
    finally { setAnnulationId(null); }
  };

  // ── Résumé — uniquement ce que le serveur a renvoyé, rien d'inventé ────────
  const estValidee = (l) => ['ok', 'approved', 'validated', 'paid'].includes(String(l.status || l.statut || ''));
  const estRefusee = (l) => ['refused', 'rejected'].includes(String(l.status || l.statut || ''));
  const joursOuvresEntre = (a, b) => {
    const da = new Date(a), db = new Date(b);
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime()) || db < da) return 0;
    let n = 0, garde = 0;
    for (let t = new Date(da); t <= db && garde < 400; t.setDate(t.getDate() + 1), garde += 1) { const w = t.getDay(); if (w !== 0 && w !== 6) n += 1; }
    return n;
  };
  const joursDemande = (l) => {
    const srv = Number(l.days ?? l.jours);
    return Number.isFinite(srv) && srv > 0 ? srv : joursOuvresEntre(l.date_debut || l.from, l.date_fin || l.to);
  };
  const liste = useMemo(() => (Array.isArray(leaves) ? leaves : []), [leaves]);
  const validees = liste.filter(estValidee);
  const refusees = liste.filter(estRefusee);
  const enCours = liste.filter(l => !estValidee(l) && !estRefusee(l));
  const joursPris = validees.reduce((somme, l) => somme + joursDemande(l), 0);
  const joursAttente = enCours.reduce((somme, l) => somme + joursDemande(l), 0);

  // Jours déjà couverts par une demande (hors refusées) → marqués dans le
  // calendrier : validé plein, en attente pointillé.
  const couverture = useMemo(() => {
    const map = {};
    for (const l of liste) {
      if (estRefusee(l)) continue;
      const a = new Date(l.date_debut || l.from), b = new Date(l.date_fin || l.to);
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) continue;
      const statut = estValidee(l) ? 'validee' : 'attente';
      let garde = 0;
      for (let t = new Date(a); t <= b && garde < 400; t.setDate(t.getDate() + 1), garde += 1) {
        const w = t.getDay(); if (w === 0 || w === 6) continue;
        const k = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        if (map[k] !== 'validee') map[k] = statut;
      }
    }
    return map;
  }, [liste]); // eslint-disable-line react-hooks/exhaustive-deps
  // Jours de la sélection DÉJÀ couverts par une demande (validée ou en
  // attente) : demander deux fois la même période créait un doublon — on
  // bloque l'envoi tant que les dates ne sont pas ajustées.
  const joursDejaPoses = useMemo(() => {
    if (!selStart || !selEnd) return 0;
    let n = 0, garde = 0; const a = new Date(selStart), b = new Date(selEnd);
    for (let t = new Date(a); t <= b && garde < 400; t.setDate(t.getDate() + 1), garde += 1) {
      const w = t.getDay(); if (w === 0 || w === 6) continue;
      const k = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
      if (couverture[k]) n += 1;
    }
    return n;
  }, [selStart, selEnd, couverture]);

  return (
    <div className="cra-layout">
      {/* Feuille — nouvelle demande sur le calendrier */}
      <section style={{ ...CARD, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '17px 18px 14px', flexWrap: 'wrap' }}>
          <div>
            <div style={LBL}>Nouvelle demande</div>
            <h2 style={{ margin: '4px 0 0', fontFamily: "'Archivo',sans-serif", fontSize: '19px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}><span style={{ textTransform: 'capitalize' }}>{mLabel(calMonth)}</span> <span style={{ fontFamily: "'IBM Plex Sans',sans-serif", fontSize: '11.5px', fontWeight: 500, letterSpacing: 0, color: 'var(--text-3)' }}>· cliquez le premier puis le dernier jour</span></h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setCalMonth(shiftMonth(calMonth, -1))} aria-label="Mois précédent"><BI icon="chevron-left" /></button>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setCalMonth(mKey(new Date()))}>Aujourd’hui</button>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setCalMonth(shiftMonth(calMonth, 1))} aria-label="Mois suivant"><BI icon="chevron-right" /></button>
          </div>
        </div>

        <div style={{ padding: '13px 18px 18px', borderTop: '1px solid var(--hair)' }}>
          {/* Légende */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', marginBottom: '12px', fontSize: '11px', color: 'var(--text-3)' }}>
            {[['sel', 'Sélection'], ['validee', 'Congé validé'], ['attente', 'Demande en attente'], ['repos', 'Week-end']].map(([k, l]) => (
              <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '9px', height: '9px', borderRadius: '2px', flex: 'none',
                  background: k === 'sel' ? 'var(--accent)' : k === 'validee' ? 'var(--erp-warning-soft, transparent)' : 'transparent',
                  border: k === 'sel' ? 'none' : k === 'validee' ? '1px solid var(--erp-warning)' : k === 'attente' ? '1px dashed var(--erp-warning)' : '1px solid var(--hair)' }} />{l}
              </span>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: '5px', marginBottom: '5px' }}>
            {WD.map(w => <span key={w} style={{ padding: '0 2px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', fontWeight: 700, color: 'var(--text-faint)' }}>{w}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: '5px' }}>
            {Array.from({ length: pads }, (_, i) => <div key={`p${i}`} />)}
            {Array.from({ length: nDays }, (_, i) => {
              const d = i + 1; const k = dayKey(d); const we = isWeekend(calMonth, d);
              const isEdge = k === selStart || k === selEnd;
              const ranged = inRange(d);
              const cover = !we ? couverture[k] : null;
              return (
                <button key={d} type="button" onClick={() => pick(d)} disabled={we}
                  aria-label={`Jour ${d}${we ? ' — week-end' : cover === 'validee' ? ' — congé validé' : cover === 'attente' ? ' — demande en attente' : ''}`}
                  title={cover === 'validee' ? 'Congé déjà validé sur cette date' : cover === 'attente' ? 'Demande déjà en attente sur cette date' : undefined}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: '3px', minHeight: '44px', padding: '5px 6px', borderRadius: '6px', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 700, userSelect: 'none', textAlign: 'left',
                    cursor: we ? 'default' : 'pointer', opacity: we ? 0.55 : 1,
                    border: we ? '1px solid var(--hair)' : cover === 'attente' && !isEdge && !ranged ? '1px dashed var(--erp-warning)' : '1px solid var(--hair)',
                    borderTop: isEdge || ranged ? '3px solid var(--accent)' : cover ? '3px solid var(--erp-warning)' : we ? '3px solid var(--hair)' : '3px solid var(--hair)',
                    background: isEdge ? 'var(--accent)' : ranged ? 'var(--accent-soft-bg)' : cover === 'validee' ? 'var(--erp-warning-soft, var(--code-bg))' : we ? 'transparent' : 'var(--code-bg)',
                    color: isEdge ? 'var(--accent-fg)' : we ? 'var(--text-faint)' : 'var(--text-strong)' }}>
                  <span className="ats-mono" style={{ fontSize: '10px', opacity: 0.85 }}>{d}</span>
                  {cover && !isEdge && !ranged && <span className="cra-day-etat" style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '8.5px', fontWeight: 700, color: 'var(--erp-warning)' }}>{cover === 'validee' ? 'Congé' : 'Att.'}</span>}
                </button>
              );
            })}
          </div>

          {/* Type + motif + envoi */}
          <div style={{ marginTop: '14px', paddingTop: '13px', borderTop: '1px solid var(--hair)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ErpiaSelect value={type} onChange={e => setType(e.target.value)} aria-label="Type de congé" style={{ ...INPUT, width: '170px', padding: '8px 10px', fontSize: '12.5px' }}>
              {leaveTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </ErpiaSelect>
            <input value={motif} onChange={e => setMotif(e.target.value)} placeholder="Motif (optionnel)" style={{ ...INPUT, flex: '1 1 160px', padding: '8px 10px', fontSize: '12.5px' }} />
          </div>
          {joursDejaPoses > 0 && selStart && selEnd && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginTop: '10px', padding: '9px 10px', borderRadius: '7px', border: '1px solid var(--danger-bd)', background: 'var(--danger-bg)', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--danger-fg)' }}>
              <BI icon="x-circle" style={{ marginTop: '1px' }} />
              <span><b>{joursDejaPoses} jour{joursDejaPoses > 1 ? 's' : ''}</b> de cette période {joursDejaPoses > 1 ? 'sont' : 'est'} déjà couvert{joursDejaPoses > 1 ? 's' : ''} par une demande de congé (validée ou en attente) — ajustez les dates : une même période ne se demande pas deux fois.</span>
            </div>
          )}
          {saisisChevauche > 0 && selStart && selEnd && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginTop: '10px', padding: '9px 10px', borderRadius: '7px', border: '1px solid var(--erp-warning)', background: 'var(--erp-warning-soft, var(--code-bg))', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--text-2)' }}>
              <BI icon="exclamation-triangle" style={{ marginTop: '1px', color: 'var(--erp-warning)' }} />
              <span><b style={{ color: 'var(--text-strong)' }}>{saisisChevauche} jour{saisisChevauche > 1 ? 's' : ''}</b> de cette période {saisisChevauche > 1 ? 'sont déjà saisis' : 'est déjà saisi'} comme travaillé{saisisChevauche > 1 ? 's' : ''} dans votre CRA — la demande reste possible, la validation du mois tranchera.</span>
            </div>
          )}
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>
              {selStart && selEnd ? <><b style={{ color: 'var(--text-strong)' }}>{workDays}</b> jour(s) ouvré(s) — {selStart} → {selEnd}</> : selStart ? `Début : ${selStart} — cliquez la fin` : 'Week-ends grisés — les jours ouvrés sont calculés.'}
            </span>
            <div style={{ flex: 1 }} />
            {(selStart || selEnd) && <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => { setSelStart(null); setSelEnd(null); }}>Effacer</button>}
            <button className="erp-btn erp-btn--primary" onClick={submit} disabled={!selStart || !selEnd || sending || joursDejaPoses > 0}
              title={joursDejaPoses > 0 ? 'La période chevauche une demande de congé existante — ajustez les dates.' : undefined}>
              <BI icon={sending ? 'arrow-repeat' : 'send'} style={sending ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Demander
            </button>
          </div>
        </div>
      </section>

      {/* Rail — récapitulatif + demandes */}
      <aside style={{ display: 'grid', gap: '16px' }}>
        <div style={{ ...CARD, padding: '16px 17px' }}>
          <div style={{ paddingBottom: '12px', borderBottom: '1px solid var(--hair)' }}>
            <div style={LBL}>Récapitulatif</div>
            <h2 style={{ margin: '4px 0 0', fontFamily: "'Archivo',sans-serif", fontSize: '16px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Mes congés</h2>
          </div>
          {leaves === undefined ? <p style={{ margin: '12px 0 0', fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</p>
            : leaves === null ? <p style={{ margin: '12px 0 0', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text-faint)' }}>L'historique des demandes est indisponible pour l'instant.</p>
            : (<>
              {(() => {
                // Solde serveur (quota posé par l'admin sur la fiche) — clé
                // optionnelle : absente, le total validé calculé reste affiché.
                const restant = Number(solde?.restant), acquis = Number(solde?.acquis), pris = Number(solde?.pris);
                const soldeOk = solde && (Number.isFinite(restant) || Number.isFinite(acquis));
                if (!soldeOk) return (
                  <div style={{ padding: '13px 0 14px' }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '27px', fontWeight: 700, letterSpacing: '-0.04em', color: 'var(--text-strong)' }}>{String(joursPris).replace('.', ',')} <span style={{ fontSize: '15px', color: 'var(--text-3)' }}>j</span></div>
                    <div style={{ marginTop: '2px', fontSize: '10.5px', color: 'var(--text-3)' }}>jours de congé validés</div>
                  </div>
                );
                const depasse = Number.isFinite(restant) && restant < 0;
                return (
                  <div style={{ padding: '13px 0 14px' }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '27px', fontWeight: 700, letterSpacing: '-0.04em', color: depasse ? 'var(--danger-fg)' : 'var(--text-strong)' }}>{Number.isFinite(restant) ? String(restant).replace('.', ',') : '—'} <span style={{ fontSize: '15px', color: 'var(--text-3)' }}>j</span></div>
                    <div style={{ marginTop: '2px', fontSize: '10.5px', color: depasse ? 'var(--danger-fg)' : 'var(--text-3)' }}>jours de congé restants{depasse ? ' — quota dépassé' : ''}</div>
                    {Number.isFinite(acquis) && acquis > 0 && (
                      <div style={{ marginTop: '9px' }}>
                        <div style={{ height: '4px', borderRadius: '3px', background: 'var(--code-bg)', overflow: 'hidden' }}><div style={{ height: '100%', width: `${Math.min(100, Math.max(0, (Number.isFinite(pris) ? pris : 0) / acquis * 100))}%`, borderRadius: 'inherit', background: depasse ? 'var(--danger-fg)' : 'var(--accent)' }} /></div>
                        <div style={{ marginTop: '4px', fontSize: '10.5px', color: 'var(--text-3)' }}>{Number.isFinite(pris) ? String(pris).replace('.', ',') : '?'} pris sur {String(acquis).replace('.', ',')} acquis cette année</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid var(--hair)', borderLeft: '1px solid var(--hair)' }}>
                {[[String(joursAttente).replace('.', ','), 'jours en attente'], [String(enCours.length), 'demandes en cours'], [String(validees.length), 'demandes validées'], [String(refusees.length), 'demandes refusées']].map(([v, l]) => (
                  <div key={l} style={{ padding: '10px 10px 9px', borderRight: '1px solid var(--hair)', borderBottom: '1px solid var(--hair)' }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>{v}</div>
                    <div style={{ marginTop: '2px', fontSize: '9.5px', lineHeight: 1.3, color: 'var(--text-3)' }}>{l}</div>
                  </div>
                ))}
              </div>
              <p style={{ margin: '11px 0 0', fontSize: '10.5px', lineHeight: 1.45, color: 'var(--text-faint)' }}>Calculé sur l'ensemble des demandes renvoyées par le serveur.</p>
            </>)}
        </div>

        <div style={{ ...CARD, padding: '16px 17px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '11px' }}>
            <span style={LBL}>Mes demandes</span>
            {Array.isArray(leaves) && <span className="ats-mono" style={{ fontSize: '10.5px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '1px 7px', borderRadius: '6px' }}>{liste.length}</span>}
          </div>
          {leaves === undefined ? <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</span>
            : leaves === null ? <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>L'historique arrive côté serveur.</span>
            : liste.length === 0 ? <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune demande — sélectionnez des dates dans le calendrier pour poser la première.</span>
            : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {liste.map((l, i) => {
                  const c = statusChip(l.status || l.statut);
                  const j = joursDemande(l);
                  return (
                    <div key={l.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typeLabel(l.type) || 'Congés'}</div>
                        <div className="ats-mono" style={{ marginTop: '2px', fontSize: '10.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtDate(l.date_debut || l.from)} → {fmtDate(l.date_fin || l.to)}</div>
                      </div>
                      {j > 0 && <span className="ats-mono" style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-2)', flex: 'none' }}>{String(j).replace('.', ',')} j</span>}
                      <Chip kind={c.k}>{c.l}</Chip>
                      {!estValidee(l) && !estRefusee(l) && l.id != null && (
                        <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={annulationId === l.id} onClick={() => annuler(l)} title="Annuler cette demande" aria-label={`Annuler la demande du ${fmtDate(l.date_debut || l.from)}`}>
                          <BI icon={annulationId === l.id ? 'arrow-repeat' : 'x-lg'} style={annulationId === l.id ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </aside>
    </div>
  );
}
// Aperçus servis par le back (justificatif de frais, document de la fiche) :
// la route preview s'ouvre avec ?cle=<clé de lecture> (iframe/onglet) et se télécharge
// avec ?download=1 en plus. On n'ouvre JAMAIS l'URL brute (justificatif_url /
// url kDrive) quand une route d'aperçu existe — c'est un lien d'application,
// pas un fichier.
const lienApercu = (base, { download = false } = {}) => {
  if (!base) return null;
  const sep = base.includes('?') ? '&' : '?';
  return avecCle(download ? `${base}${sep}download=1` : base);
};

// ── Notes de frais : liste + ajout (justificatif optionnel → multipart) ──────
function Frais({ showNotification }) {
  const { items: frais, load: reloadFrais } = useMeList('frais');
  const [label, setLabel] = useState('');
  const [montant, setMontant] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const add = async () => {
    if (!label.trim() || !montant) return;
    setSending(true);
    try {
      let r;
      if (file) {
        const fd = new FormData();
        fd.append('label', label.trim()); fd.append('montant', montant); fd.append('justificatif', file);
        r = await fetch(`${BASE}/resources/me/frais`, { method: 'POST', headers: authH(), body: fd });
      } else {
        r = await fetch(`${BASE}/resources/me/frais`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ label: label.trim(), montant: Number(montant) }) });
      }
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Note de frais ajoutée.', 'success');
      setLabel(''); setMontant(''); setFile(null);
      reloadFrais();
    } catch (e) { showNotification?.('Erreur note de frais : ' + e.message, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ ...CARD, padding: '16px 18px' }}>
      <div style={{ ...LBL, marginBottom: '10px' }}>Notes de frais</div>
      <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Libellé — ex. Transport client Lausanne" style={{ ...INPUT, flex: '1 1 220px' }} />
        <input value={montant} onChange={e => setMontant(e.target.value)} type="number" min="0" step="0.05" placeholder="CHF" style={{ ...INPUT, width: '110px', fontFamily: "'IBM Plex Mono',monospace" }} />
        <button onClick={() => fileRef.current?.click()} className="src-row-hover" title="Joindre un justificatif (photo/PDF)"
          style={{ ...GHOST_BTN, maxWidth: '190px', overflow: 'hidden' }}>
          <BI icon="paperclip" /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file ? file.name : 'Justificatif'}</span>
          {file && <span onClick={(e) => { e.stopPropagation(); setFile(null); }} style={{ display: 'inline-flex', color: 'var(--text-faint)' }}><BI icon="x" /></span>}
        </button>
        <button onClick={add} disabled={sending || !label.trim() || !montant} style={{ ...PRIMARY_BTN, opacity: (sending || !label.trim() || !montant) ? 0.5 : 1 }}>
          <BI icon={sending ? 'arrow-repeat' : 'plus-lg'} style={sending ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Ajouter
        </button>
      </div>
      <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {frais === undefined && <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</span>}
        {Array.isArray(frais) && frais.length === 0 && <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune note de frais.</span>}
        {Array.isArray(frais) && frais.map((f, i) => {
          const c = statusChip(f.status || f.statut);
          return (
            <div key={f.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}{f.date && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}> · {fmtDate(f.date)}</span>}</span>
              {f.justificatif_preview_url ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flex: 'none' }}>
                  <a href={lienApercu(f.justificatif_preview_url)} target="_blank" rel="noreferrer" title="Voir le justificatif" style={{ color: 'var(--text-2)', fontSize: '12px' }}><BI icon="eye" /></a>
                  <a href={lienApercu(f.justificatif_preview_url, { download: true })} target="_blank" rel="noreferrer" title="Télécharger le justificatif" style={{ color: 'var(--text-2)', fontSize: '12px' }}><BI icon="download" /></a>
                </span>
              ) : f.justificatif_url ? (
                /* Jamais de lien direct vers justificatif_url (application
                   kDrive) : tant que la route d'aperçu ne couvre pas cette
                   ligne, on montre la présence du justificatif sans l'ouvrir. */
                <span title="Justificatif joint — l'aperçu arrive côté serveur" style={{ color: 'var(--text-faint)', fontSize: '12px' }}><BI icon="paperclip" /></span>
              ) : null}
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-2)', flex: 'none' }}>{(f.montant ?? f.amount) != null ? `${f.montant ?? f.amount} ${f.devise || 'CHF'}` : '—'}</span>
              <Chip kind={c.k}>{c.l}</Chip>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Documents de la fiche (is_principal en premier côté back) ────────────────
function Docs() {
  const { items: docs } = useMeList('documents');
  return (
    <div style={{ ...CARD, padding: '16px 18px' }}>
      <div style={{ ...LBL, marginBottom: '10px' }}>Mes documents</div>
      {docs === undefined ? <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</span>
        : !docs || docs.length === 0 ? <span style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>CV, contrats et attestations apparaîtront ici.</span>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {docs.map((d, i) => (
                <div key={d.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'var(--code-bg)', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="file-earmark-text" style={{ fontSize: '13px' }} /></span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.label || d.original_name || d.name}
                    {d.is_principal && <Chip kind="mut" style={{ marginLeft: '7px', fontSize: '10px' }}>principal</Chip>}
                  </span>
                  {(d.uploaded_at || d.date) && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-faint)', flex: 'none' }}>{fmtDate(d.uploaded_at || d.date)}</span>}
                  {d.preview_url ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flex: 'none' }}>
                      <a href={lienApercu(d.preview_url)} target="_blank" rel="noreferrer" className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 10px', fontSize: '11.5px', textDecoration: 'none' }}><BI icon="eye" style={{ fontSize: '11px' }} /> Ouvrir</a>
                      <a href={lienApercu(d.preview_url, { download: true })} target="_blank" rel="noreferrer" className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 10px', fontSize: '11.5px', textDecoration: 'none' }}><BI icon="download" style={{ fontSize: '11px' }} /> Télécharger</a>
                    </span>
                  ) : d.url ? (
                    /* Repli : document sans route d'aperçu (ancien enregistrement). */
                    <a href={d.url} target="_blank" rel="noreferrer" className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 10px', fontSize: '11.5px', textDecoration: 'none' }}><BI icon="download" style={{ fontSize: '11px' }} /> Télécharger</a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
    </div>
  );
}

// ── Page « Mon espace » ───────────────────────────────────────────────────────
const TABS = { cra: 'Mon CRA', conges: 'Congés', frais: 'Notes de frais', docs: 'Documents' };

export default function MonEspace({ tab = 'cra', showNotification, onOuvrirConges }) {
  const [me, setMe] = useState(undefined); // undefined = chargement, null = pas de fiche liée
  // Catalogue des types de congé de l'organisation — repli sur la liste fixe
  // tant que la route n'existe pas côté serveur.
  const { items: typesSrv } = useMeList('leave-types');
  const leaveTypes = useMemo(() => normLeaveTypes(typesSrv), [typesSrv]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/resources/me`, { headers: authH() });
        if (!r.ok) { if (!cancelled) setMe(null); return; }
        const j = await r.json().catch(() => ({}));
        if (!cancelled) setMe(j.data || j);
      } catch { if (!cancelled) setMe(null); }
    })();
    return () => { cancelled = true; };
  }, []);

  const fullName = me ? [me.prenom, me.nom].filter(Boolean).join(' ') : '';

  // Statut de mission — affiché uniquement quand le serveur le donne.
  const statutActif = me?.statut && /actif|active|en[_ ]cours/i.test(String(me.statut));

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Fil d'ariane + en-tête — coque « espace consultant » de la maquette */}
      <div className="ats-mono me-crumb" style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '14px' }}>Mon espace / <span style={{ color: 'var(--text-2)' }}>{TABS[tab] || 'Mon CRA'}</span></div>
      <div className="me-hero" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <div style={{ ...LBL, marginBottom: '5px' }}>Mon activité</div>
          <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>{TABS[tab] || 'Mon CRA'}</h1>
          {tab === 'cra' && <p className="me-sub" style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'var(--text-2)' }}>Déclarez vos journées, vérifiez le récapitulatif, puis soumettez votre CRA au client.</p>}
        </div>
        {me?.statut && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: statutActif ? 'var(--erp-success-soft, var(--chip-bg))' : 'var(--chip-bg)', color: statutActif ? 'var(--erp-success, var(--text-2))' : 'var(--text-2)', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', marginBottom: '4px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', flex: 'none' }} />{me.statut}
          </span>
        )}
      </div>

      {me === null && (
        <div style={{ ...CARD, marginBottom: '16px', padding: '12px 16px', fontSize: '12.5px', color: 'var(--text-2)' }}>
          <BI icon="info-circle" style={{ marginRight: '7px' }} />Aucune fiche ressource liée à votre compte pour l'instant — contactez votre référent RH.
        </div>
      )}

      {/* Bandeau mission — cellules séparées par un filet, comme la maquette */}
      {me && (
        <div className="cra-mission-strip" style={{ ...CARD, marginBottom: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span className="ats-mono" style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'var(--chip-bg)', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flex: 'none' }}>{((me.prenom?.[0] || '') + (me.nom?.[0] || '')).toUpperCase() || '—'}</span>
            <div style={{ minWidth: 0 }}>
              <b style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName || 'Ressource'}</b>
              <span style={{ fontSize: '10.5px', color: 'var(--text-3)' }}>{me.taux_activite ? `Taux d'activité ${me.taux_activite}%` : 'Ressource en mission'}</span>
            </div>
          </div>
          <div><div style={LBL}>Mission</div><b style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={me.mission_titre || undefined}>{me.mission_titre || '—'}</b></div>
          <div><div style={LBL}>Client</div><b style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={me.client || undefined}>{me.client || '—'}</b></div>
          <div><div style={LBL}>Période</div><b className="ats-mono" style={{ display: 'block', marginTop: '4px', fontSize: '11.5px', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(me.date_debut || me.date_fin) ? `${fmtDate(me.date_debut) || '…'} — ${fmtDate(me.date_fin) || '…'}` : '—'}</b></div>
        </div>
      )}

      {tab === 'cra' && <Timesheet showNotification={showNotification} unit={craUnitFrom(me, me?.mission)} leaveTypes={leaveTypes} onOuvrirConges={onOuvrirConges} missionTitre={me?.mission_titre} />}
      {tab === 'conges' && <Conges showNotification={showNotification} leaveTypes={leaveTypes} solde={me?.solde_conges} />}
      {tab === 'frais' && <Frais showNotification={showNotification} />}
      {tab === 'docs' && <Docs />}
    </div>
  );
}
